import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';

// PrimeNG
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

// Services
import { NotificationService, Notification } from '../../../core/services/notification.service';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [
    CommonModule,
    DrawerModule,
    ButtonModule,
    TooltipModule
  ],
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.scss']
})
export class NotificationPanelComponent implements OnInit, OnDestroy {
  unreadCount$: Observable<number>;
  notifications: Notification[] = [];
  loading = false;
  notificationDrawerVisible = false;
  private notificationsSubscription?: Subscription;

  constructor(
    private notificationService: NotificationService,
    private router: Router
  ) {
    this.unreadCount$ = this.notificationService.getUnreadCount();
  }

  ngOnInit(): void {
    // Subscribe to notifications observable to get real-time updates
    this.notificationsSubscription = this.notificationService.getNotificationsObservable().subscribe(
      (notifications) => {
        this.notifications = notifications.slice(0, 10); // Limit to 10 for display
      }
    );
    
    // Load notifications initially
    this.loadNotifications();
  }

  ngOnDestroy(): void {
    if (this.notificationsSubscription) {
      this.notificationsSubscription.unsubscribe();
    }
  }

  loadNotifications(): void {
    this.loading = true;
    this.notificationService.getNotifications({ limit: 10 }).subscribe({
      next: (response) => {
        if (response.success) {
          this.notifications = response.data;
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading notifications:', error);
        this.loading = false;
      }
    });
  }

  onDrawerOpen(): void {
    // Refresh notifications when drawer opens to ensure latest data
    this.loadNotifications();
  }

  handleNotificationClick(notification: Notification): void {
    // Close the notification drawer immediately
    this.notificationDrawerVisible = false;

    // Mark as read if not already
    if (!notification.isRead) {
      this.notificationService.markAsRead(notification._id).subscribe({
        next: () => {
          // Update local state
          notification.isRead = true;
        }
      });
    }

    // Mark as viewed
    if (!notification.isViewed) {
      this.notificationService.markAsViewed(notification._id).subscribe();
    }

    // Navigate if action URL exists
    if (notification.metadata?.actionUrl) {
      const url = notification.metadata.actionUrl;
      const [path, queryString] = url.split('?');
      const queryParams: any = {};
      
      if (queryString) {
        queryString.split('&').forEach(param => {
          const [key, value] = param.split('=');
          if (key && value) {
            queryParams[decodeURIComponent(key)] = decodeURIComponent(value);
          }
        });
      }
      
      this.router.navigate([path], { queryParams });
    }
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        // Refresh notifications after marking all as read
        this.loadNotifications();
      },
      error: (error) => {
        console.error('Error marking all as read:', error);
      }
    });
  }

  viewAllNotifications(): void {
    this.router.navigate(['/admin/notifications']);
  }

  getIconClass(notification: Notification): string {
    return this.notificationService.getIconClass(notification.type);
  }

  getPriorityClass(priority: string): string {
    return `priority-${priority.toLowerCase()}`;
  }

  getTimeAgo(date: Date): string {
    const now = new Date().getTime();
    const notifDate = new Date(date).getTime();
    const diff = now - notifDate;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return new Date(date).toLocaleDateString();
  }
}

